import BaseQuery from "@/stateManagement/BaseQuery";
import type { Observable } from "rxjs";
import { combineLatest, filter, map } from "rxjs";
import { singleton } from "tsyringe";

import RewardsProgramAccountQuery from "../../ProgramAccount/query/RewardsProgramAccountQuery";
import { VIP_TIERS_CONDITION_TYPES } from "../constants";
import type { VipTierEntity } from "../entities/RewardsVipTiersEntities";
import type { VipTiersState } from "../stores/RewardsVipTiersStore";
import RewardsVipTiersStore from "../stores/RewardsVipTiersStore";
import { getCustomerTierIndex } from "../utils";

@singleton()
class RewardsVipTiersQuery extends BaseQuery<VipTiersState> {
    constructor(
        protected readonly store: RewardsVipTiersStore,
        private readonly programAccountQuery: RewardsProgramAccountQuery
    ) {
        super(store);
    }

    tierList$ = this.select((state) => state.tierList);
    hasTiers$ = this.select((state) => state.tierList.length > 0);
    firstTier$ = this.select((state) => state.tierList[0]);
    status$ = this.select((state) => state.status);
    isCustomerTagCondition$ = this.firstTier$.pipe(
        filter((tier) => !!tier),
        map((tier) => tier.condition === VIP_TIERS_CONDITION_TYPES.customerTag)
    );

    public selectAvailableTiers(): Observable<VipTierEntity[]> {
        return combineLatest([
            this.tierList$,
            this.programAccountQuery.customerCurrentTier$,
        ]).pipe(
            map(([tierList, customerTier]) =>
                tierList.slice(
                    Math.max(0, getCustomerTierIndex(tierList, customerTier))
                )
            )
        );
    }

    public selectCustomerNextTier(): Observable<VipTierEntity> {
        return combineLatest([
            this.tierList$,
            this.programAccountQuery.customerCurrentTier$,
        ]).pipe(
            filter(([tierList]) => !!tierList.length),
            map(([tierList, customerTier]) => {
                const index = getCustomerTierIndex(tierList, customerTier);

                return tierList[index + 1];
            })
        );
    }
}

export default RewardsVipTiersQuery;
